<template>
  <div class="bulk-stitch-approval">
    <!-- Season Auto-Suggest Section -->
    <div class="box">
      <div class="level">
        <div class="level-left">
          <h4 class="subtitle is-5">
            <span class="icon"><i class="mdi mdi-folder-search"></i></span>
            Auto-Suggest Source Files
          </h4>
        </div>
        <div class="level-right">
          <b-field grouped>
            <b-select v-model="selectedStorageConfigId" placeholder="Select Storage Config" :loading="loadingConfigs">
              <option v-for="config in storageConfigs" :key="config.id" :value="config.id">
                {{ config.config_name }} ({{ config.bucket_name || config.base_folder || 'No path' }})
              </option>
            </b-select>
            <b-tooltip label="Learn pattern from an example filename (season-specific)" position="is-bottom">
              <b-button
                type="is-link"
                icon-left="school"
                outlined
                @click="openLearnPatternModal"
              >
                Learn Pattern
              </b-button>
            </b-tooltip>
            <b-button
              type="is-primary"
              icon-left="magnify"
              :loading="suggesting"
              :disabled="!selectedStorageConfigId"
              @click="runAutoSuggest"
            >
              Auto-Suggest All
            </b-button>
            <b-button
              v-if="segmentStats && segmentStats.linked > 0"
              type="is-warning"
              icon-left="link-off"
              :loading="unlinking"
              @click="unlinkAllFiles"
            >
              Unlink All
            </b-button>
          </b-field>
        </div>
      </div>

      <!-- Show learned pattern and/or path if set -->
      <div v-if="learnedPattern || learnedPath" class="notification is-light is-link mt-2 py-2">
        <div class="level is-mobile">
          <div class="level-left">
            <span class="icon"><i class="mdi mdi-school"></i></span>
            <span v-if="learnedPattern" class="ml-2"><strong>Pattern:</strong> <code>{{ learnedPattern }}</code></span>
            <b-tag v-if="learnedPattern" type="is-info" class="ml-2">{{ Math.round(learnedPatternConfidence * 100) }}%</b-tag>
            <span v-if="learnedPath" class="ml-3"><strong>Path:</strong> <code>{{ learnedPath }}</code></span>
          </div>
          <div class="level-right">
            <b-button type="is-text" size="is-small" icon-left="close" @click="clearLearnedPattern">
              Clear
            </b-button>
          </div>
        </div>
      </div>

      <!-- Suggestion Results -->
      <div v-if="suggestionResults" class="suggestion-results">
        <div class="level">
          <div class="level-item has-text-centered">
            <div>
              <p class="heading">Episodes Scanned</p>
              <p class="title">{{ suggestionResults.stats.totalEpisodes }}</p>
            </div>
          </div>
          <div class="level-item has-text-centered">
            <div>
              <p class="heading">Segments Matched</p>
              <p class="title has-text-success">{{ suggestionResults.stats.segmentsMatched }}</p>
            </div>
          </div>
          <div class="level-item has-text-centered">
            <div>
              <p class="heading">Not Found</p>
              <p class="title has-text-warning">{{ suggestionResults.stats.segmentsNotFound }}</p>
            </div>
          </div>
          <div class="level-item has-text-centered">
            <div>
              <p class="heading">Duration Mismatches</p>
              <p class="title has-text-danger">{{ suggestionResults.stats.durationMismatches }}</p>
            </div>
          </div>
        </div>

        <!-- Episode/Segment Details - Flat Table View -->
        <div v-if="flattenedSuggestions && flattenedSuggestions.length > 0" class="mt-4">
          <h5 class="subtitle is-6 mb-2">
            <span class="icon"><i class="mdi mdi-format-list-bulleted"></i></span>
            Segment Suggestions (Review Before Linking)
          </h5>

          <b-table
            :data="flattenedSuggestions"
            :hoverable="true"
            :striped="true"
            :narrowed="true"
            :paginated="flattenedSuggestions.length > 25"
            :per-page="25"
            class="suggestion-table"
          >
            <template #default="props">
              <b-table-column field="episodeNumber" label="Ep" width="50" sortable numeric>
                {{ props.row.episodeNumber }}
              </b-table-column>
              <b-table-column field="segmentNumber" label="Seg" width="50" sortable numeric>
                {{ props.row.segmentNumber }}
              </b-table-column>
              <b-table-column field="linkStatus" label="Status" width="90">
                <b-tag v-if="props.row.linkStatus === 'verified'" type="is-success" size="is-small">Verified</b-tag>
                <b-tag v-else-if="props.row.linkStatus === 'linked'" type="is-info" size="is-small">Linked</b-tag>
                <b-tag v-else-if="props.row.linkStatus === 'suggested'" type="is-primary" size="is-small">Found</b-tag>
                <b-tag v-else type="is-warning" size="is-small">Not Found</b-tag>
              </b-table-column>
              <b-table-column field="expectedDurationMs" label="Expected" width="80">
                {{ formatSecondsAsDuration(props.row.expectedDurationMs / 1000) }}
              </b-table-column>
              <b-table-column field="actualDurationMs" label="Actual" width="80">
                <template v-if="props.row.actualDurationMs">
                  {{ formatSecondsAsDuration(props.row.actualDurationMs / 1000) }}
                </template>
                <span v-else class="has-text-grey">-</span>
              </b-table-column>
              <b-table-column field="durationMismatch" label="Diff" width="80">
                <span v-if="props.row.durationMismatch" class="has-text-danger has-text-weight-bold">
                  {{ props.row.durationMismatch.diffFormatted }}
                </span>
                <span v-else-if="props.row.linkStatus === 'suggested'" class="has-text-success">
                  <b-icon icon="check" size="is-small" />
                </span>
                <span v-else class="has-text-grey">-</span>
              </b-table-column>
              <b-table-column field="suggestedFileName" label="Source File">
                <span v-if="props.row.suggestedFileName" class="filename-cell" :title="props.row.suggestedFileName">
                  {{ props.row.suggestedFileName }}
                </span>
                <span v-else class="has-text-grey-light is-italic">No match found</span>
              </b-table-column>
            </template>
          </b-table>
        </div>

        <div v-if="suggestionResults.stats.segmentsMatched > 0" class="has-text-centered has-margin-top-15">
          <p v-if="suggestionResults.stats.durationMismatches > 0" class="has-text-warning mb-3">
            <b-icon icon="alert" size="is-small" />
            {{ suggestionResults.stats.durationMismatches }} segments have duration mismatches. After linking, you'll need to approve them.
          </p>
          <b-button
            type="is-success"
            icon-left="link"
            size="is-medium"
            :loading="linking"
            @click="applyAllSuggestions"
          >
            Link {{ suggestionResults.stats.segmentsMatched }} Matched Files
          </b-button>
        </div>
      </div>
    </div>

    <!-- Status Summary -->
    <div v-if="segmentStats" class="box">
      <h4 class="subtitle is-5">
        <span class="icon"><i class="mdi mdi-check-all"></i></span>
        Segment Source File Status
      </h4>

      <div class="level">
        <div class="level-item has-text-centered">
          <div>
            <p class="heading">Total Segments</p>
            <p class="title">{{ segmentStats.totalSegments }}</p>
          </div>
        </div>
        <div class="level-item has-text-centered">
          <div>
            <p class="heading">Not Linked</p>
            <p class="title has-text-grey">{{ segmentStats.unlinked }}</p>
          </div>
        </div>
        <div class="level-item has-text-centered">
          <div>
            <p class="heading">Pending Verification</p>
            <p class="title has-text-info">{{ segmentStats.pendingVerification }}</p>
          </div>
        </div>
        <div class="level-item has-text-centered">
          <div>
            <p class="heading">Needs Approval</p>
            <p class="title has-text-warning">{{ segmentStats.withMismatch }}</p>
          </div>
        </div>
        <div class="level-item has-text-centered">
          <div>
            <p class="heading">Verified</p>
            <p class="title has-text-success">{{ segmentStats.verified }}</p>
          </div>
        </div>
      </div>

      <!-- Progress Bar -->
      <progress
        class="progress is-success"
        :value="segmentStats.verified"
        :max="segmentStats.totalSegments"
      >{{ verifiedPercentage }}%</progress>
      <p class="has-text-centered has-text-grey-light">
        {{ verifiedPercentage }}% verified ({{ segmentStats.verified }}/{{ segmentStats.totalSegments }})
      </p>
    </div>

    <!-- Pending Approval Table - Using mismatches from getSeasonSegmentStats -->
    <div v-if="mismatches && mismatches.length > 0" class="box">
      <div class="level">
        <div class="level-left">
          <h4 class="subtitle is-5">
            <span class="icon"><i class="mdi mdi-alert-circle"></i></span>
            Duration Mismatches Needing Approval ({{ mismatches.length }})
          </h4>
        </div>
        <div class="level-right">
          <b-button
            type="is-success"
            icon-left="check-all"
            :loading="bulkApproving"
            @click="bulkApproveAll"
          >
            Approve All {{ mismatches.length }} Mismatches
          </b-button>
        </div>
      </div>

      <p class="has-text-grey mb-3">
        <b-icon icon="information" size="is-small" />
        Approving a mismatch will update the segment duration to match the actual source file duration.
      </p>

      <b-table
        :data="mismatches"
        :hoverable="true"
        :striped="true"
        :narrowed="true"
        :paginated="mismatches.length > 15"
        :per-page="15"
        default-sort="episodeNumber"
      >
        <template #default="props">
          <b-table-column field="episodeNumber" label="Ep #" width="60" sortable>
            {{ props.row.episodeNumber }}
          </b-table-column>
          <b-table-column field="segmentNumber" label="Seg #" width="60" sortable>
            {{ props.row.segmentNumber }}
          </b-table-column>
          <b-table-column field="expectedSeconds" label="Expected" width="100">
            {{ formatSecondsAsDuration(props.row.expectedSeconds) }}
          </b-table-column>
          <b-table-column field="actualSeconds" label="Actual (Source)" width="120">
            {{ formatSecondsAsDuration(props.row.actualSeconds) }}
          </b-table-column>
          <b-table-column field="diffSeconds" label="Difference" width="100">
            <span :class="getDifferenceClass(props.row.diffSeconds)">
              {{ formatDifferenceSeconds(props.row.diffSeconds) }}
            </span>
          </b-table-column>
          <b-table-column label="Actions" width="150">
            <div class="buttons are-small">
              <b-button
                type="is-success"
                size="is-small"
                icon-left="check"
                :loading="approvingSegment === props.row.segmentId"
                @click="approveSegment(props.row)"
              >
                Approve
              </b-button>
            </div>
          </b-table-column>
        </template>
      </b-table>
    </div>

    <!-- No Pending Mismatches -->
    <div v-else-if="mismatches && mismatches.length === 0 && segmentStats && segmentStats.linked > 0" class="box has-text-centered">
      <span class="icon is-large has-text-success">
        <i class="mdi mdi-check-circle mdi-48px"></i>
      </span>
      <p class="subtitle">No duration mismatches pending approval</p>
      <p class="has-text-grey">All linked segments have matching durations or have been approved.</p>
    </div>

    <!-- Episode-Level Breakdown -->
    <div v-if="episodesData && episodesData.length > 0" class="box">
      <h4 class="subtitle is-5">
        <span class="icon"><i class="mdi mdi-format-list-bulleted"></i></span>
        Per-Episode Segment Status
      </h4>

      <b-table
        :data="sortedEpisodesData"
        :hoverable="true"
        :striped="true"
        :narrowed="true"
        :paginated="sortedEpisodesData.length > 20"
        :per-page="20"
      >
        <template #default="props">
          <b-table-column field="episode_number" label="Ep #" width="60">
            {{ props.row.episode_number }}
          </b-table-column>
          <b-table-column field="episode_title" label="Episode Title">
            {{ props.row.episode_title || `Episode ${props.row.episode_number}` }}
          </b-table-column>
          <b-table-column field="totalSegments" label="Segments" width="80" numeric>
            {{ props.row.totalSegments }}
          </b-table-column>
          <b-table-column field="linked" label="Linked" width="80" numeric>
            <span :class="props.row.linked === props.row.totalSegments ? 'has-text-success' : 'has-text-warning'">
              {{ props.row.linked }}/{{ props.row.totalSegments }}
            </span>
          </b-table-column>
          <b-table-column field="verified" label="Verified" width="80" numeric>
            <span :class="props.row.verified === props.row.totalSegments ? 'has-text-success' : ''">
              {{ props.row.verified }}/{{ props.row.totalSegments }}
            </span>
          </b-table-column>
          <b-table-column field="status" label="Status" width="120">
            <b-tag v-if="props.row.allVerified" type="is-success" size="is-small">Ready</b-tag>
            <b-tag v-else-if="props.row.linked === 0" type="is-light" size="is-small">Not Linked</b-tag>
            <b-tag v-else-if="props.row.verified < props.row.linked" type="is-warning" size="is-small">Needs Approval</b-tag>
            <b-tag v-else type="is-info" size="is-small">Partial</b-tag>
          </b-table-column>
          <b-table-column field="stitch_status" label="Stitch" width="100">
            <b-tag v-if="props.row.stitch_status === 'completed'" type="is-success" size="is-small">Done</b-tag>
            <b-tag v-else-if="props.row.stitch_status === 'processing'" type="is-info" size="is-small">Processing</b-tag>
            <b-tag v-else-if="props.row.stitch_status === 'failed'" type="is-danger" size="is-small">Failed</b-tag>
            <b-tag v-else type="is-light" size="is-small">Pending</b-tag>
          </b-table-column>
        </template>
      </b-table>
    </div>

    <!-- Season Stitch Job Submission -->
    <div v-if="segmentStats && segmentStats.verified > 0" class="box">
      <div class="level">
        <div class="level-left">
          <h4 class="subtitle is-5">
            <span class="icon"><i class="mdi mdi-movie-roll"></i></span>
            Submit Stitch Jobs for Season
          </h4>
        </div>
      </div>

      <!-- Episode Selection -->
      <div class="episode-selection">
        <div class="level">
          <div class="level-left">
            <b-checkbox v-model="selectAllEpisodes" @input="toggleSelectAll">
              Select All Ready Episodes
            </b-checkbox>
          </div>
          <div class="level-right">
            <b-tag type="is-info">{{ selectedEpisodeIds.length }} episodes selected</b-tag>
          </div>
        </div>

        <div class="columns is-multiline has-margin-top-10">
          <div
            v-for="ep in readyEpisodes"
            :key="ep.id"
            class="column is-3"
          >
            <b-checkbox
              v-model="selectedEpisodeIds"
              :native-value="ep.id"
            >
              <span class="is-size-7">
                E{{ ep.episode_number }} - {{ ep.title }}
                <b-tag v-if="ep.stitch_status === 'completed'" type="is-success" size="is-small">Done</b-tag>
                <b-tag v-else-if="ep.stitch_status === 'processing'" type="is-warning" size="is-small">Processing</b-tag>
              </span>
            </b-checkbox>
          </div>
        </div>

        <div v-if="readyEpisodes.length === 0" class="has-text-centered has-text-grey">
          <p>No episodes are ready for stitching. Link and verify all segments first.</p>
        </div>
      </div>

      <!-- Workflow Selection -->
      <div v-if="readyEpisodes.length > 0" class="has-margin-top-15">
        <b-field grouped>
          <b-field label="Processing Workflow" expanded>
            <b-select v-model="selectedWorkflowId" placeholder="Select workflow" expanded>
              <option v-for="workflow in processingWorkflows" :key="workflow.id" :value="workflow.id">
                {{ workflow.template_name }}
              </option>
            </b-select>
          </b-field>
          <b-field label="Output Storage" expanded>
            <b-select v-model="selectedOutputStorageId" placeholder="Select output storage" expanded>
              <option v-for="config in outputStorageConfigs" :key="config.id" :value="config.id">
                {{ config.config_name }}
              </option>
            </b-select>
          </b-field>
        </b-field>

        <div class="has-text-centered has-margin-top-15">
          <b-button
            type="is-success"
            size="is-large"
            icon-left="movie-roll"
            :loading="submittingJobs"
            :disabled="selectedEpisodeIds.length === 0 || !selectedOutputStorageId"
            @click="submitSeasonStitchJobs"
          >
            Submit {{ selectedEpisodeIds.length }} Stitch Jobs
          </b-button>
        </div>
      </div>
    </div>

    <!-- Learn Pattern Modal -->
    <b-modal :active.sync="showLearnPatternModal" has-modal-card trap-focus>
      <template #default>
        <div class="modal-card" style="width: 600px;">
          <header class="modal-card-head">
            <p class="modal-card-title">
              <span class="icon"><i class="mdi mdi-school"></i></span>
              Learn Pattern from Example
            </p>
            <button type="button" class="delete" @click="showLearnPatternModal = false" />
          </header>
          <section class="modal-card-body">
            <div class="notification is-info is-light">
              <p>Paste an example filename from your source files. The system will analyze it to learn the naming pattern for this season.</p>
              <p class="mt-2 is-size-7"><strong>Note:</strong> Each season can have its own pattern - this complements the storage config pattern.</p>
            </div>

            <b-field label="Example Filename *" message="Include the full filename without the folder path">
              <b-input
                v-model="exampleFilename"
                placeholder="e.g., ReaTsotella_Season25_E001_Seg1_08M24S.ts"
                expanded
              />
            </b-field>

            <b-field label="Search Folder Path (optional)" message="Limit auto-suggest to files in this subfolder (relative to storage config base folder)">
              <b-input
                v-model="learnedPath"
                placeholder="e.g., ReaTsotella/Season25 or leave empty to search all folders"
                expanded
              />
            </b-field>

            <div class="columns">
              <div class="column is-half">
                <b-field label="Episode Number (for this example)">
                  <b-numberinput
                    v-model="exampleEpisodeNumber"
                    :min="1"
                    :max="9999"
                    controls-position="compact"
                  />
                </b-field>
              </div>
              <div class="column is-half">
                <b-field label="Segment Number (for this example)">
                  <b-numberinput
                    v-model="exampleSegmentNumber"
                    :min="1"
                    :max="99"
                    controls-position="compact"
                  />
                </b-field>
              </div>
            </div>

            <!-- Show learned pattern result -->
            <div v-if="learnedPattern" class="mt-4">
              <div class="notification" :class="learnedPatternConfidence >= 0.75 ? 'is-success' : learnedPatternConfidence >= 0.5 ? 'is-warning' : 'is-danger'" style="background-color: inherit;">
                <p class="has-text-weight-bold">Learned Pattern:</p>
                <code class="is-size-5">{{ learnedPattern }}</code>
                <p class="mt-2 is-size-7">
                  Confidence: {{ Math.round(learnedPatternConfidence * 100) }}%
                  <span v-if="learnedPatternMatchedParts">
                    | Matched: {{ Object.keys(learnedPatternMatchedParts).join(', ') }}
                  </span>
                </p>
              </div>

              <div class="content is-small mt-3">
                <p><strong>Placeholders found:</strong></p>
                <ul>
                  <li v-if="learnedPattern.includes('{SHOW}')"><code>{SHOW}</code> - Show title</li>
                  <li v-if="learnedPattern.includes('{S}') || learnedPattern.includes('{SS}')"><code>{SS}</code> - Season number</li>
                  <li v-if="learnedPattern.includes('{E}') || learnedPattern.includes('{EE}')"><code>{EEE}</code> - Episode number</li>
                  <li v-if="learnedPattern.includes('{N}')"><code>{N}</code> - Segment number</li>
                  <li v-if="learnedPattern.includes('{*}')"><code>{*}</code> - Variable content (duration, ID, etc.)</li>
                </ul>
              </div>
            </div>
          </section>
          <footer class="modal-card-foot">
            <b-button @click="showLearnPatternModal = false">Cancel</b-button>
            <b-button
              type="is-link"
              icon-left="magnify"
              :loading="learningPattern"
              :disabled="!exampleFilename"
              @click="learnPattern"
            >
              Analyze Pattern
            </b-button>
            <b-button
              v-if="learnedPattern && learnedPatternConfidence >= 0.5"
              type="is-success"
              icon-left="check"
              @click="applyLearnedPattern"
            >
              Use This Pattern
            </b-button>
          </footer>
        </div>
      </template>
    </b-modal>
  </div>
</template>

<script>
import stitchJobsApi from '@/api/stitchJobs'
import storageConfigApi from '@/api/storageConfig'
import storageBrowseApi from '@/api/storageBrowse'
import workflowTemplatesApi from '@/api/workflowTemplates'

export default {
  name: 'BulkStitchApproval',
  props: {
    mediaId: {
      type: [Number, String],
      required: true
    },
    seasonNumber: {
      type: [Number, String],
      default: null
    },
    // Media title for pattern learning - passed from parent
    mediaTitle: {
      type: String,
      default: ''
    }
  },
  data () {
    return {
      loading: false,
      bulkApproving: false,
      approvingSegment: null,
      status: null,
      segmentStats: null,
      // Mismatches data from getSeasonSegmentStats
      mismatches: [],
      episodesData: [],
      // Storage configs
      storageConfigs: [],
      outputStorageConfigs: [],
      loadingConfigs: false,
      selectedStorageConfigId: null,
      // Auto-suggest
      suggesting: false,
      suggestionResults: null,
      linking: false,
      unlinking: false,
      // Episode selection for stitch
      readyEpisodes: [],
      selectedEpisodeIds: [],
      selectAllEpisodes: true,
      // Stitch submission
      processingWorkflows: [],
      selectedWorkflowId: null,
      selectedOutputStorageId: null,
      submittingJobs: false,
      // Learn pattern from example state (season-specific)
      showLearnPatternModal: false,
      learningPattern: false,
      exampleFilename: '',
      exampleEpisodeNumber: 1,
      exampleSegmentNumber: 1,
      learnedPattern: null,
      learnedPatternConfidence: 0,
      learnedPatternMatchedParts: null,
      // Optional subfolder path for auto-suggest (set via Learn Pattern modal)
      learnedPath: ''
    }
  },
  computed: {
    verifiedPercentage () {
      if (!this.segmentStats || this.segmentStats.totalSegments === 0) return 0
      return Math.round((this.segmentStats.verified / this.segmentStats.totalSegments) * 100)
    },
    /**
     * Sort episodes data numerically by episode_number
     */
    sortedEpisodesData () {
      if (!this.episodesData || this.episodesData.length === 0) return []
      return [...this.episodesData].sort((a, b) => {
        const numA = parseInt(a.episode_number, 10) || 0
        const numB = parseInt(b.episode_number, 10) || 0
        return numA - numB
      })
    },
    /**
     * Sort suggestion episodes numerically by episodeNumber
     */
    sortedSuggestionEpisodes () {
      if (!this.suggestionResults || !this.suggestionResults.episodes) return []
      return [...this.suggestionResults.episodes].sort((a, b) => {
        const numA = parseInt(a.episodeNumber, 10) || 0
        const numB = parseInt(b.episodeNumber, 10) || 0
        return numA - numB
      })
    },
    /**
     * Flatten suggestion results into a single array for the table
     * Each row = one segment with episode info
     */
    flattenedSuggestions () {
      if (!this.suggestionResults || !this.suggestionResults.episodes) return []

      const rows = []
      for (const episode of this.suggestionResults.episodes) {
        for (const seg of episode.segments) {
          // Get suggested file info from either format
          const suggestedFile = seg.suggestedFile || seg.suggestion
          rows.push({
            episodeId: episode.episodeId,
            episodeNumber: episode.episodeNumber,
            episodeTitle: episode.episodeTitle,
            segmentId: seg.segmentId,
            segmentNumber: seg.segmentNumber,
            linkStatus: seg.linkStatus,
            expectedDurationMs: seg.expectedDurationMs,
            actualDurationMs: suggestedFile?.durationMs || suggestedFile?.detectedDurationMs || null,
            durationMismatch: seg.durationMismatch,
            suggestedFileName: suggestedFile ? (suggestedFile.name || suggestedFile.key?.split('/').pop()) : null,
            suggestedFilePath: suggestedFile?.key || suggestedFile?.path || null
          })
        }
      }
      // Sort by episode number then segment number (numeric)
      return rows.sort((a, b) => {
        const epA = parseInt(a.episodeNumber, 10) || 0
        const epB = parseInt(b.episodeNumber, 10) || 0
        if (epA !== epB) return epA - epB
        const segA = parseInt(a.segmentNumber, 10) || 0
        const segB = parseInt(b.segmentNumber, 10) || 0
        return segA - segB
      })
    }
  },
  watch: {
    mediaId: {
      immediate: true,
      handler () {
        this.loadAll()
      }
    }
  },
  methods: {
    async loadAll () {
      if (!this.mediaId) return
      await Promise.all([
        this.loadStatus(),
        this.loadSegmentStats(),
        this.loadStorageConfigs(),
        this.loadProcessingWorkflows()
      ])
    },

    async loadStatus () {
      this.loading = true
      try {
        // Get verification status from stitchJobs API for pendingApproval list
        this.status = await stitchJobsApi.getVerificationStatus(this.mediaId, this.seasonNumber)
      } catch (error) {
        console.error('Failed to load verification status:', error)
        this.status = { pendingApproval: [], statusCounts: {} }
      } finally {
        this.loading = false
      }
    },

    async loadSegmentStats () {
      try {
        // Get segment stats from the new API endpoint
        const statsData = await stitchJobsApi.getSeasonSegmentStats(this.mediaId, this.seasonNumber)
        this.segmentStats = statsData.stats || {
          totalSegments: 0,
          linked: 0,
          unlinked: 0,
          verified: 0,
          pendingVerification: 0,
          withMismatch: 0
        }

        // Store mismatches for detailed table display
        this.mismatches = statsData.mismatches || []

        // Store episodes data for per-episode breakdown
        this.episodesData = statsData.episodes || []

        // Build ready episodes list from the episodes data
        if (statsData.episodes) {
          this.readyEpisodes = statsData.episodes
            .filter(ep => ep.allVerified && ep.stitch_status !== 'completed')
            .map(ep => ({
              id: ep.episode_id,
              episode_number: ep.episode_number,
              title: ep.episode_title,
              stitch_status: ep.stitch_status
            }))

          // Pre-select all ready episodes
          this.selectedEpisodeIds = this.readyEpisodes.map(ep => ep.id)
        }
      } catch (error) {
        console.error('Failed to load segment stats:', error)
        this.segmentStats = {
          totalSegments: 0,
          linked: 0,
          unlinked: 0,
          verified: 0,
          pendingVerification: 0,
          withMismatch: 0
        }
        this.mismatches = []
        this.episodesData = []
        this.readyEpisodes = []
      }
    },

    async loadStorageConfigs () {
      this.loadingConfigs = true
      try {
        const configs = await storageConfigApi.getAll()
        // Filter source configs - archive/library configs are used for source file scanning
        // config_name contains the purpose (e.g., "archive", "library", "tx_mezzanine")
        this.storageConfigs = configs.filter(c =>
          c.is_active !== false &&
          (c.config_name?.toLowerCase().includes('archive') ||
           c.config_name?.toLowerCase().includes('library') ||
           c.config_name?.toLowerCase().includes('source') ||
           c.config_name?.toLowerCase().includes('ingest') ||
           // Show all configs if no specific naming convention found
           configs.length <= 5)
        )
        // If no matches, show all active configs
        if (this.storageConfigs.length === 0) {
          this.storageConfigs = configs.filter(c => c.is_active !== false)
        }
        // Output configs for stitch destination
        this.outputStorageConfigs = configs.filter(c =>
          c.is_active !== false &&
          (c.config_name?.toLowerCase().includes('tx') ||
           c.config_name?.toLowerCase().includes('mezzanine') ||
           c.config_name?.toLowerCase().includes('vod') ||
           c.config_name?.toLowerCase().includes('delivery') ||
           c.config_name?.toLowerCase().includes('output'))
        )
        // If no output matches, show all active configs
        if (this.outputStorageConfigs.length === 0) {
          this.outputStorageConfigs = configs.filter(c => c.is_active !== false)
        }

        // Auto-select first configs if available
        if (this.storageConfigs.length > 0) {
          this.selectedStorageConfigId = this.storageConfigs[0].id
        }
        if (this.outputStorageConfigs.length > 0) {
          this.selectedOutputStorageId = this.outputStorageConfigs[0].id
        }
      } catch (error) {
        console.error('Failed to load storage configs:', error)
      } finally {
        this.loadingConfigs = false
      }
    },

    async loadProcessingWorkflows () {
      try {
        const response = await workflowTemplatesApi.getAvailable()
        this.processingWorkflows = response.workflows || []
        if (this.processingWorkflows.length > 0) {
          // Select default workflow if available
          const defaultWorkflow = this.processingWorkflows.find(w => w.is_default)
          this.selectedWorkflowId = defaultWorkflow ? defaultWorkflow.id : this.processingWorkflows[0].id
        }
      } catch (error) {
        console.error('Failed to load workflow templates:', error)
      }
    },

    async unlinkAllFiles () {
      if (!this.segmentStats || this.segmentStats.linked === 0) {
        this.$buefy.toast.open({
          message: 'No linked files to unlink',
          type: 'is-info'
        })
        return
      }

      this.$buefy.dialog.confirm({
        title: 'Unlink All Source Files',
        message: `<p>This will unlink <strong>${this.segmentStats.linked}</strong> source files from their segments.</p>
          <p class="has-text-warning mt-2">After unlinking, you can re-run Auto-Suggest to match new files.</p>`,
        confirmText: 'Unlink All',
        type: 'is-warning',
        hasIcon: true,
        icon: 'link-off',
        onConfirm: async () => {
          this.unlinking = true
          try {
            const result = await stitchJobsApi.bulkUnlinkSegments(this.mediaId, this.seasonNumber)

            this.$buefy.toast.open({
              message: `Unlinked ${result.unlinked} files. You can now re-run Auto-Suggest.`,
              type: 'is-success',
              duration: 5000
            })

            // Clear suggestion results and reload stats
            this.suggestionResults = null
            await this.loadAll()
            this.$emit('unlinked')
          } catch (error) {
            console.error('Unlink failed:', error)
            this.$buefy.toast.open({
              message: error?.data?.error || 'Failed to unlink files',
              type: 'is-danger'
            })
          } finally {
            this.unlinking = false
          }
        }
      })
    },

    // ==========================================
    // Learn Pattern from Example
    // ==========================================

    openLearnPatternModal () {
      this.exampleFilename = ''
      this.exampleEpisodeNumber = 1
      this.exampleSegmentNumber = 1
      // Don't clear learned pattern - keep it visible in modal for reference
      this.showLearnPatternModal = true
    },

    async learnPattern () {
      if (!this.exampleFilename) {
        this.$buefy.toast.open({
          message: 'Please enter an example filename',
          type: 'is-warning'
        })
        return
      }

      this.learningPattern = true
      try {
        const result = await storageBrowseApi.learnPatternFromExample(
          this.exampleFilename,
          this.mediaTitle || '',
          this.seasonNumber || null,
          this.exampleEpisodeNumber || null,
          this.exampleSegmentNumber || null
        )

        if (result.success) {
          this.learnedPattern = result.pattern
          this.learnedPatternConfidence = result.confidence
          this.learnedPatternMatchedParts = result.matchedParts

          if (result.confidence < 0.5) {
            this.$buefy.toast.open({
              message: 'Low confidence pattern - some parts could not be identified',
              type: 'is-warning'
            })
          }
        } else {
          this.$buefy.toast.open({
            message: 'Could not learn pattern from this filename',
            type: 'is-danger'
          })
        }
      } catch (error) {
        console.error('Failed to learn pattern:', error)
        this.$buefy.toast.open({
          message: 'Failed to analyze filename pattern',
          type: 'is-danger'
        })
      } finally {
        this.learningPattern = false
      }
    },

    applyLearnedPattern () {
      if (this.learnedPattern) {
        this.$buefy.toast.open({
          message: `Pattern learned: ${this.learnedPattern}`,
          type: 'is-success'
        })
        this.showLearnPatternModal = false
      }
    },

    clearLearnedPattern () {
      this.learnedPattern = null
      this.learnedPatternConfidence = 0
      this.learnedPatternMatchedParts = null
      this.learnedPath = ''
      this.$buefy.toast.open({
        message: 'Learned pattern and path cleared',
        type: 'is-info'
      })
    },

    // ==========================================
    // Auto-Suggest
    // ==========================================

    async runAutoSuggest () {
      if (!this.selectedStorageConfigId) {
        this.$buefy.toast.open({
          message: 'Please select a storage configuration',
          type: 'is-warning'
        })
        return
      }

      this.suggesting = true
      this.suggestionResults = null

      try {
        // Pass learned pattern (season-specific) and optional path if available
        const result = await stitchJobsApi.suggestSeasonFiles(
          this.mediaId,
          this.seasonNumber,
          this.selectedStorageConfigId,
          this.learnedPattern, // customPattern - learned from example, season-specific
          this.learnedPath // optional subfolder path to limit search
        )
        this.suggestionResults = result

        if (result.stats.segmentsMatched === 0) {
          this.$buefy.toast.open({
            message: 'No matching files found. Try using Learn Pattern to teach the system your naming convention.',
            type: 'is-warning',
            duration: 5000
          })
        } else {
          this.$buefy.toast.open({
            message: `Found ${result.stats.segmentsMatched} matching files!`,
            type: 'is-success'
          })
        }
      } catch (error) {
        console.error('Auto-suggest failed:', error)
        this.$buefy.toast.open({
          message: error?.data?.error || 'Auto-suggest failed',
          type: 'is-danger'
        })
      } finally {
        this.suggesting = false
      }
    },

    async applyAllSuggestions () {
      if (!this.suggestionResults || this.suggestionResults.stats.segmentsMatched === 0) return

      this.linking = true
      try {
        // Collect all suggested links using the SAME format as episode-level auto-suggest
        // This ensures full S3 paths are preserved (including folder structure)
        const links = []
        for (const episode of this.suggestionResults.episodes) {
          for (const seg of episode.segments) {
            // Use suggestedFile or fallback to suggestion object
            const file = seg.suggestedFile || seg.suggestion
            if (file && !seg.alreadyLinked) {
              links.push({
                segmentId: seg.segmentId,
                storageConfigId: this.selectedStorageConfigId,
                sourcePath: file.path || file.key,  // Full S3 path with folders
                sourceFilename: file.name
              })
            }
          }
        }

        if (links.length === 0) {
          this.$buefy.toast.open({
            message: 'No new files to link',
            type: 'is-info'
          })
          return
        }

        // Use the same endpoint as episode-level auto-suggest (linkSourceFiles)
        // This method correctly preserves full S3 paths with folder structure
        const result = await stitchJobsApi.linkSourceFiles(links)

        this.$buefy.toast.open({
          message: `Linked ${result.linked} segments. ${result.mismatches?.length || 0} duration mismatches need approval.`,
          type: 'is-success',
          duration: 5000
        })

        // Reload data
        await this.loadAll()
        this.suggestionResults = null
        this.$emit('approved')
      } catch (error) {
        console.error('Bulk link failed:', error)
        this.$buefy.toast.open({
          message: error?.data?.error || 'Failed to link files',
          type: 'is-danger'
        })
      } finally {
        this.linking = false
      }
    },

    toggleSelectAll (value) {
      if (value) {
        this.selectedEpisodeIds = this.readyEpisodes.map(ep => ep.id)
      } else {
        this.selectedEpisodeIds = []
      }
    },

    async submitSeasonStitchJobs () {
      if (this.selectedEpisodeIds.length === 0) {
        this.$buefy.toast.open({
          message: 'Please select at least one episode',
          type: 'is-warning'
        })
        return
      }

      this.$buefy.dialog.confirm({
        title: 'Submit Stitch Jobs',
        message: `<p>This will submit <strong>${this.selectedEpisodeIds.length}</strong> stitch jobs for processing.</p>
          <p class="has-text-grey">Jobs will be queued independently - one failure won't affect others.</p>`,
        confirmText: 'Submit Jobs',
        type: 'is-success',
        hasIcon: true,
        icon: 'movie-roll',
        onConfirm: async () => {
          this.submittingJobs = true
          try {
            const result = await stitchJobsApi.submitSeasonJobs(
              this.mediaId,
              this.selectedEpisodeIds,
              {
                processingWorkflowId: this.selectedWorkflowId,
                outputStorageConfigId: this.selectedOutputStorageId
              }
            )

            const message = `Submitted: ${result.stats.submitted}, Skipped: ${result.stats.skipped}, Errors: ${result.stats.errors}`
            this.$buefy.toast.open({
              message: message,
              type: result.stats.errors > 0 ? 'is-warning' : 'is-success',
              duration: 5000
            })

            // Reload to show updated status
            await this.loadAll()
            this.$emit('approved')
          } catch (error) {
            console.error('Failed to submit season jobs:', error)
            this.$buefy.toast.open({
              message: error?.data?.error || 'Failed to submit stitch jobs',
              type: 'is-danger'
            })
          } finally {
            this.submittingJobs = false
          }
        }
      })
    },

    async bulkApproveAll () {
      if (!this.mismatches || this.mismatches.length === 0) {
        this.$buefy.toast.open({
          message: 'No mismatches to approve',
          type: 'is-info'
        })
        return
      }

      this.$buefy.dialog.confirm({
        title: 'Bulk Approve All Mismatches',
        message: `<p>This will approve <strong>${this.mismatches.length}</strong> duration mismatches.</p>
          <p class="has-text-grey">Segment durations will be updated to match actual file durations.</p>`,
        confirmText: 'Approve All',
        type: 'is-success',
        hasIcon: true,
        icon: 'check-all',
        onConfirm: async () => {
          this.bulkApproving = true
          try {
            // Use segmentId from mismatches array (from getSeasonSegmentStats API)
            const segmentIds = this.mismatches.map(s => s.segmentId)
            const result = await stitchJobsApi.bulkApproveSegmentMismatches(
              segmentIds,
              'Bulk approved from Manage Episodes UI'
            )

            this.$buefy.toast.open({
              message: `Approved ${result.approved} duration mismatches`,
              type: 'is-success',
              duration: 5000
            })

            // Reload data
            await this.loadAll()
            this.$emit('approved')
          } catch (error) {
            console.error('Failed to bulk approve:', error)
            this.$buefy.toast.open({
              message: 'Failed to bulk approve mismatches',
              type: 'is-danger'
            })
          } finally {
            this.bulkApproving = false
          }
        }
      })
    },

    async approveSegment (segment) {
      // Use segmentId from mismatches array (from getSeasonSegmentStats API)
      this.approvingSegment = segment.segmentId
      try {
        await stitchJobsApi.approveSegmentMismatch(segment.segmentId, 'Approved from bulk approval UI')
        this.$buefy.toast.open({
          message: `Approved E${segment.episodeNumber} Seg ${segment.segmentNumber}`,
          type: 'is-success'
        })
        await this.loadAll()
      } catch (error) {
        console.error('Failed to approve segment:', error)
        this.$buefy.toast.open({
          message: 'Failed to approve mismatch',
          type: 'is-danger'
        })
      } finally {
        this.approvingSegment = null
      }
    },

    async rejectSegment (segment) {
      this.$buefy.dialog.confirm({
        title: 'Reject Duration Mismatch',
        message: `Rejecting will unlink the source file from E${segment.episode_number} Segment ${segment.segment_number}. The correct file will need to be linked.`,
        confirmText: 'Reject',
        type: 'is-danger',
        onConfirm: async () => {
          try {
            await stitchJobsApi.rejectMismatch(segment.id, 'Rejected from bulk approval UI')
            this.$buefy.toast.open({
              message: 'Mismatch rejected - please link correct file',
              type: 'is-info'
            })
            await this.loadAll()
          } catch (error) {
            console.error('Failed to reject segment:', error)
            this.$buefy.toast.open({
              message: 'Failed to reject mismatch',
              type: 'is-danger'
            })
          }
        }
      })
    },

    formatDuration (seconds) {
      if (!seconds) return '0s'
      const mins = Math.floor(seconds / 60)
      const secs = Math.round(seconds % 60)
      return mins > 0 ? `${mins}m ${secs}s` : `${secs}s`
    },

    formatSecondsAsDuration (seconds) {
      // Format seconds as MM:SS or HH:MM:SS
      if (seconds === null || seconds === undefined) return '-'
      const totalSeconds = Math.round(seconds)
      const hours = Math.floor(totalSeconds / 3600)
      const mins = Math.floor((totalSeconds % 3600) / 60)
      const secs = totalSeconds % 60
      if (hours > 0) {
        return `${hours}:${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`
      }
      return `${mins}:${String(secs).padStart(2, '0')}`
    },

    formatDifference (seconds) {
      if (seconds === null || seconds === undefined) return '-'
      const sign = seconds >= 0 ? '+' : ''
      return `${sign}${seconds.toFixed(2)}s`
    },

    formatDifferenceSeconds (diffSeconds) {
      // Format duration difference with sign
      if (diffSeconds === null || diffSeconds === undefined) return '-'
      const sign = diffSeconds >= 0 ? '+' : ''
      const absDiff = Math.abs(diffSeconds)
      if (absDiff < 1) {
        return `${sign}${diffSeconds.toFixed(2)}s`
      }
      // For larger differences, show MM:SS format
      const mins = Math.floor(absDiff / 60)
      const secs = Math.round(absDiff % 60)
      if (mins > 0) {
        return `${sign}${mins}m ${secs}s`
      }
      return `${sign}${secs}s`
    },

    getDifferenceClass (diff) {
      if (!diff) return ''
      if (Math.abs(diff) < 0.1) return 'has-text-success'
      if (Math.abs(diff) < 1) return 'has-text-warning'
      return 'has-text-danger has-text-weight-bold'
    },

    /**
     * Get status type for episode suggestion card
     */
    getEpisodeStatusType (episode) {
      if (!episode.segments) return 'is-light'
      const allFound = episode.segments.every(s => s.linkStatus === 'suggested' || s.linkStatus === 'verified' || s.linkStatus === 'linked')
      const hasMissing = episode.segments.some(s => s.linkStatus === 'unlinked')
      const hasMismatches = episode.segments.some(s => s.durationMismatch)

      if (hasMissing) return 'is-warning'
      if (hasMismatches) return 'is-danger'
      if (allFound) return 'is-success'
      return 'is-light'
    },

    /**
     * Get status text for episode suggestion card
     */
    getEpisodeStatusText (episode) {
      if (!episode.segments) return 'Unknown'
      const found = episode.segments.filter(s => s.linkStatus === 'suggested' || s.linkStatus === 'verified' || s.linkStatus === 'linked').length
      const total = episode.segments.length
      const mismatches = episode.segments.filter(s => s.durationMismatch).length

      if (mismatches > 0) return `${mismatches} duration diff${mismatches > 1 ? 's' : ''}`
      if (found === total) return 'All Found'
      if (found === 0) return 'None Found'
      return `${found}/${total} Found`
    },

    /**
     * Custom numeric sort for b-table
     */
    numericSort (a, b, key) {
      const valA = parseInt(a[key], 10) || 0
      const valB = parseInt(b[key], 10) || 0
      return valA - valB
    },

    /**
     * Get actual duration from segment's suggested file (avoids optional chaining in template)
     */
    getSegmentActualDuration (seg) {
      if (seg.suggestedFile && seg.suggestedFile.durationMs) {
        return seg.suggestedFile.durationMs
      }
      if (seg.suggestion && seg.suggestion.durationMs) {
        return seg.suggestion.durationMs
      }
      return 0
    },

    /**
     * Get filename from segment's suggested file (avoids optional chaining in template)
     */
    getSegmentFileName (seg) {
      let name = ''
      if (seg.suggestedFile && seg.suggestedFile.name) {
        name = seg.suggestedFile.name
      } else if (seg.suggestion && seg.suggestion.name) {
        name = seg.suggestion.name
      }
      if (!name) return ''
      // Get just the filename from path
      const parts = name.split('/')
      return parts[parts.length - 1] || name
    }
  },

  mounted () {
    // Load workflows and storage configs when component mounts
    this.loadProcessingWorkflows()
    this.loadStorageConfigs()
  }
}
</script>

<style scoped>
.bulk-stitch-approval {
  margin-top: 1rem;
}

.box {
  color: #363636;
}

.progress {
  margin-bottom: 0.5rem;
}

.suggestion-results {
  margin-top: 1rem;
  padding: 1rem;
  background-color: #f5f5f5;
  border-radius: 6px;
}

/* Suggestion table - flat list view */
.suggestion-table {
  font-size: 0.85rem;
}

.suggestion-table .filename-cell {
  font-family: monospace;
  font-size: 0.8rem;
  color: #3273dc;
  word-break: break-all;
}

.episode-selection {
  max-height: 300px;
  overflow-y: auto;
  padding: 1rem;
  background-color: #fafafa;
  border-radius: 6px;
}

.mt-4 {
  margin-top: 1rem;
}

.mb-2 {
  margin-bottom: 0.5rem;
}

.mb-3 {
  margin-bottom: 0.75rem;
}

.ml-2 {
  margin-left: 0.5rem;
}
</style>
